import { defineComponent as _defineComponent } from 'vue'
import { vModelText as _vModelText, createElementVNode as _createElementVNode, withDirectives as _withDirectives, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = { class: "freelance-search" }
const _hoisted_2 = { class: "search-card" }
const _hoisted_3 = ["id", "onSubmit"]
const _hoisted_4 = { class: "inputs" }
const _hoisted_5 = { class: "category" }
const _hoisted_6 = ["aria-label", "label", "placeholder", "data-heap"]
const _hoisted_7 = {
  key: 0,
  class: "category-error"
}
const _hoisted_8 = ["onMousedown"]
const _hoisted_9 = { class: "location c-autocomplete" }
const _hoisted_10 = ["aria-label", "label", "placeholder", "data-heap"]
const _hoisted_11 = { class: "links" }
const _hoisted_12 = ["data-heap"]
const _hoisted_13 = {
  key: 0,
  class: "submit-project"
}
const _hoisted_14 = { class: "between-ctas" }
const _hoisted_15 = ["href", "data-heap"]

import {ref, onMounted, onBeforeUnmount} from 'vue';
    import {KeyTextField} from '@prismicio/types';
    import {defaultCitiesSuggestions, LocationAutocomplete} from '@malt/location-autocomplete';
    import {LocationSuggestion, QuerySuggestion} from '../types/custom';
    import {useTypeGuards} from '../composables/useTypeGuards';
    import {useSearchHelpers} from '../composables/useSearchHelpers';

    type Props = {
        id: string;
        categoryLabel: KeyTextField;
        locationLabel: KeyTextField;
        searchLinkText: KeyTextField;
        searchLink: KeyTextField;
        submitProjectLinkText?: KeyTextField;
        submitProjectLink?: KeyTextField;
        betweenCtasText?: KeyTextField;
        missingCategoryErrorText?: KeyTextField;
        heapPrefix?: string;
        remoteInCountryLabel?: KeyTextField;
        remoteInEuropeLabel?: KeyTextField;
        locale: string;
        searchPage?: boolean;
        searchAutocompleteApiV2?: boolean;
        amplitudeTracking?: boolean;
        briefOrigin?: string;
    };

    
export default /*#__PURE__*/_defineComponent({
  __name: 'FreelanceSearch',
  props: {
    id: {},
    categoryLabel: {},
    locationLabel: {},
    searchLinkText: {},
    searchLink: {},
    submitProjectLinkText: { default: '' },
    submitProjectLink: { default: '' },
    betweenCtasText: { default: '' },
    missingCategoryErrorText: { default: '' },
    heapPrefix: { default: '' },
    remoteInCountryLabel: { default: '' },
    remoteInEuropeLabel: { default: '' },
    locale: {},
    searchPage: { type: Boolean, default: false },
    searchAutocompleteApiV2: { type: Boolean, default: false },
    amplitudeTracking: { type: Boolean, default: false },
    briefOrigin: { default: '' }
  },
  setup(__props: any) {

const props = __props;

    

    const [lang, countryCode] = props.locale?.split('-') ?? ['fr', 'FR'];

    const {isHTMLInputElement, isLocationSuggestionRecord, isLocationSuggestionResponse, isFilled} = useTypeGuards();
    const {locationAsRecord, recordAsLocation, getQueryAutocomplete, getSearchURL} = useSearchHelpers();
    const defaultLocationSuggestions: LocationSuggestion[] = [];

    if (isLocationSuggestionRecord(defaultCitiesSuggestions)) {
        const localeSuggestions: LocationSuggestion[] | undefined = defaultCitiesSuggestions[countryCode];

        if (isLocationSuggestionResponse(localeSuggestions)) {
            defaultLocationSuggestions.push(...localeSuggestions);
        }
    }

    const query = ref('');
    const locationInputRef = ref<HTMLInputElement | null>(null);
    const locationInput = ref('');
    const location = ref<LocationSuggestion | null>(null);
    const submitProjectLinkWithParams = ref<string>(computeSubmitProjectLinkWithParams());
    const categoryIsInError = ref(false);
    const querySuggestions = ref<QuerySuggestion[] | string[]>([]);
    const isQuerySearchActive = ref(false);
    let locationAutocomplete: any = null;

    const searchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(searchParams);

    const defaultAddress = ref<LocationSuggestion>({
        formattedAddress: params.location || props.remoteInCountryLabel || '',
        remoteAllowed: !!params.remoteAllowed || !!props.remoteInCountryLabel,
        remoteEuropa: !!params.remoteEuropa || !!props.remoteInEuropeLabel,
    });

    function onSearchReset() {
        location.value = null;

        const payload: Record<string, string> = {q: query.value};
        if (locationInput.value) {
            payload.location = locationInput.value;
        }

        Hop.events.trigger('navbar:searchRequested', {
            reset: true,
            payload,
        });
    }

    onMounted(() => {
        if (params.q) {
            query.value = params.q;
        }

        if (!locationInputRef.value) {
            return;
        }

        if (params.location) {
            location.value = recordAsLocation(params);
            locationInput.value = params.location;
            locationInputRef.value.value = params.location;
        }

        locationAutocomplete = new LocationAutocomplete({
            allowRemote: !!props.remoteInCountryLabel,
            allowRemoteEuropa: !!props.remoteInEuropeLabel,
            countryCode,
            defaultCitySuggestions: defaultLocationSuggestions,
            defaultValue: defaultAddress.value,
            displaySuggestions: true,
            input: locationInputRef.value,
            languageCode: lang,
            layer: 'LOCALITY',
            remoteIfEmpty: true,
            remoteCountryOptionLabel: props.remoteInCountryLabel,
            remoteEuropaLabel: props.remoteInEuropeLabel,
            onSelect: (selectedAddress: LocationSuggestion) => {
                locationInput.value = selectedAddress.formattedAddress || '';
                location.value = selectedAddress;
            },
        });

        if (!params.location) {
            locationInputRef.value.value = '';
        }

        Hop.events.on('navbar:searchResetRequested', onSearchReset);
    });

    onBeforeUnmount(() => {
        locationAutocomplete.destroy();

        Hop.events.off('navbar:searchResetRequested', onSearchReset);
    });

    function onSubmit() {
        if (!isFilled(query.value)) {
            categoryIsInError.value = true;
            return;
        }

        const locationRecord = locationAsRecord(location.value);

        if (props.searchPage) {
            // We don't want to reload the page when we're on a page with search results (freelancers)
            Hop.events.trigger('navbar:searchRequested', {
                reset: false,
                payload: {
                    ...locationRecord,
                    q: query.value,
                    location: locationInput.value,
                },
            });
            return;
        }

        window.location.href = getSearchURL({
            baseURLLink: props.searchLink,
            query: query.value,
            location: locationRecord,
            ...(props.amplitudeTracking ? {sourceComponent: 'navbar_search_expanded'} : {}),
        });
    }

    async function onQueryInputChange(event: Event) {
        if (!isHTMLInputElement(event.target)) {
            return;
        }

        query.value = event.target.value ?? '';

        if (props.submitProjectLink) {
            submitProjectLinkWithParams.value = getSearchURL({
                baseURLLink: props.submitProjectLink,
                query: query.value,
                location: locationAsRecord(location.value),
                ...(props.amplitudeTracking ? {sourceComponent: 'navbar_search_expanded'} : {}),
            });
        }

        if (event.target.value.length >= 2) {
            querySuggestions.value = await getQueryAutocomplete(event.target.value, props.searchAutocompleteApiV2);
            isQuerySearchActive.value = true;
        } else {
            querySuggestions.value = [];
        }
    }

    function onQuerySuggestionClick(event: Event, querySuggestion: QuerySuggestion | string) {
        event.preventDefault();
        query.value = props.searchAutocompleteApiV2 ? (querySuggestion as string) : (querySuggestion as QuerySuggestion).text;

        if (props.submitProjectLink) {
            submitProjectLinkWithParams.value = getSearchURL({
                baseURLLink: props.submitProjectLink,
                query: query.value,
                location: locationAsRecord(location.value),
                ...(props.amplitudeTracking ? {sourceComponent: 'navbar_search_expanded'} : {}),
            });
        }

        setTimeout(() => {
            isQuerySearchActive.value = false;
        }, 100);
    }

    function onQueryBlur() {
        isQuerySearchActive.value = false;
    }

    function clearQuery() {
        query.value = '';
        querySuggestions.value = [];
    }

    function clearLocation() {
        location.value = null;
        locationInput.value = '';
    }

    function computeSubmitProjectLinkWithParams() {
        if (props.submitProjectLink) {
            if (props.briefOrigin) {
                const url = props.submitProjectLink;
                if (!url.includes('brief_origin')) {
                    return `${url}${url.includes('?') ? '&' : '?'}brief_origin=${props.briefOrigin}&showDispatchScreen=true`;
                }
            }

            return props.submitProjectLink;
        }
        return '';
    }

return (_ctx: any,_cache: any) => {
  const _component_joy_icon = _resolveComponent("joy-icon")!

  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("form", {
        id: _ctx.id,
        class: "search-form",
        "data-testid": "search-block-form",
        onSubmit: _withModifiers(onSubmit, ["prevent"])
      }, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((query).value = $event)),
              class: "category-input",
              name: "category-input",
              type: "text",
              "data-testid": "search-block-query-field",
              "aria-label": _ctx.categoryLabel || 'category',
              label: _ctx.categoryLabel,
              placeholder: _ctx.categoryLabel || 'category',
              "data-heap": `${_ctx.heapPrefix}_click-input_skills-first-input-field-new-cms`,
              autocomplete: "off",
              list: "query-list",
              debounce: "500",
              onInput: onQueryInputChange,
              onFocus: _cache[1] || (_cache[1] = 
                                () => {
                                    isQuerySearchActive.value = true;
                                }
                            ),
              onBlur: onQueryBlur
            }, null, 40, _hoisted_6), [
              [_vModelText, query.value]
            ]),
            _createElementVNode("button", {
              class: _normalizeClass({'clear-button': true, visible: query.value.length > 0}),
              type: "button",
              "data-testid": "search-block-query-clear",
              "aria-label": "clear-query",
              onClick: clearQuery
            }, [
              _createVNode(_component_joy_icon, {
                name: "cross",
                clickable: ""
              })
            ], 2),
            (categoryIsInError.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(_ctx.missingCategoryErrorText), 1))
              : _createCommentVNode("", true),
            _createElementVNode("div", {
              class: _normalizeClass({
                                suggestions: true,
                                visible: isQuerySearchActive.value && querySuggestions.value.length > 0,
                            }),
              "data-testid": "search-block-query-suggestions"
            }, [
              _createElementVNode("ul", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(querySuggestions.value, (querySuggestion) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: typeof querySuggestion === 'string' ? querySuggestion : querySuggestion.key,
                    onMousedown: (event) => onQuerySuggestionClick(event, querySuggestion)
                  }, _toDisplayString(typeof querySuggestion === 'string' ? querySuggestion : querySuggestion.text), 41, _hoisted_8))
                }), 128))
              ])
            ], 2)
          ]),
          _createElementVNode("div", _hoisted_9, [
            _withDirectives(_createElementVNode("input", {
              ref_key: "locationInputRef",
              ref: locationInputRef,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((locationInput).value = $event)),
              class: "location-input",
              name: "location-input",
              type: "text",
              maxlength: "500",
              "aria-label": _ctx.locationLabel || 'location',
              "data-testid": "search-block-location-field",
              label: _ctx.locationLabel,
              placeholder: _ctx.locationLabel || 'location',
              "data-heap": `${_ctx.heapPrefix}_click-input_location-second-input-field-new-cms`,
              autocomplete: "off"
            }, null, 8, _hoisted_10), [
              [_vModelText, locationInput.value]
            ]),
            _createElementVNode("button", {
              class: _normalizeClass({
                                'clear-button': true,
                                visible: locationInput.value.length > 0,
                            }),
              type: "button",
              "data-testid": "search-block-location-clear",
              "aria-label": "clear-location",
              onClick: clearLocation
            }, [
              _createVNode(_component_joy_icon, {
                name: "cross",
                clickable: ""
              })
            ], 2)
          ])
        ]),
        _createElementVNode("div", _hoisted_11, [
          _createElementVNode("button", {
            type: "submit",
            "data-heap": `${_ctx.heapPrefix}_submit-cta-button_search-launch-new-cms`,
            "data-testid": "search-block-btn-submit"
          }, _toDisplayString(_ctx.searchLinkText), 9, _hoisted_12),
          (_ctx.submitProjectLink)
            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                _createElementVNode("span", _hoisted_14, _toDisplayString(_ctx.betweenCtasText), 1),
                _createElementVNode("a", {
                  href: submitProjectLinkWithParams.value,
                  "data-heap": `${_ctx.heapPrefix}_click-cta-button_post-project-new-cms`
                }, _toDisplayString(_ctx.submitProjectLinkText), 9, _hoisted_15)
              ]))
            : _createCommentVNode("", true)
        ])
      ], 40, _hoisted_3)
    ]),
    _renderSlot(_ctx.$slots, "reassurance")
  ]))
}
}

})